// WP-7.2/7.3 (RE-4, QT-6): PDF export — the FIRST PDF capability this app has ever needed.
// Unlike CSV/XLSX (hand-rolled, zero-dependency — real PDF syntax/font handling is not
// reasonably hand-rollable the same way), `jspdf` is a genuinely new dependency, added only
// now that a real need exists, the SAME "add a library only when it's actually needed"
// precedent `echarts` already established (WP-5.3, "the first charting library"). Kept
// entirely client-side, reusing the EXACT SAME `TabularData` shape `toCsv`/`toXlsx` already
// consume (packages/engine/src/domain/exportTable.ts) — no server-side route, no parallel
// data-shaping, only the rendering format differs from the existing CSV/XLSX exporters.
import { jsPDF } from 'jspdf';
import type { TabularData } from '@pfos/engine';
import { formatMoney, fromMinor } from '@pfos/engine';
import type { QuotationResponse } from '@pfos/shared';

const MARGIN_X = 14;
const LINE_HEIGHT = 7;

/** RE-4: a lightweight tabular PDF — title, column headers, rows, and the SAME footnotes
 * (incl. the valuation-basis line) already embedded in `data.footnotes` by the existing
 * CSV/XLSX builders (`buildReportExport`/`buildTransactionsExport`), never re-worded here. */
export function buildReportPdf(title: string, data: TabularData): jsPDF {
  const doc = new jsPDF();
  let y = 18;
  doc.setFontSize(16);
  doc.text(title, MARGIN_X, y);
  y += LINE_HEIGHT + 4;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(data.columns.map((c) => c.label).join('    '), MARGIN_X, y);
  y += LINE_HEIGHT;
  doc.setFont('helvetica', 'normal');
  for (const row of data.rows) {
    const line = data.columns.map((c) => String(row[c.key] ?? '')).join('    ');
    doc.text(line, MARGIN_X, y);
    y += LINE_HEIGHT;
  }

  if (data.footnotes !== undefined && data.footnotes.length > 0) {
    y += 4;
    doc.setFontSize(8);
    for (const note of data.footnotes) {
      doc.text(note, MARGIN_X, y);
      y += LINE_HEIGHT - 2;
    }
  }

  return doc;
}

export function downloadReportPdf(filename: string, title: string, data: TabularData): void {
  buildReportPdf(title, data).save(filename);
}

function pricingLine(quotation: QuotationResponse): string {
  if (quotation.pricing.kind === 'fixed') {
    return formatMoney(fromMinor(quotation.currency, quotation.pricing.amountMinor));
  }
  const min = formatMoney(fromMinor(quotation.currency, quotation.pricing.minMinor));
  const max = formatMoney(fromMinor(quotation.currency, quotation.pricing.maxMinor));
  return `${min}–${max}`;
}

/** QT-6: "a lightweight shareable summary ... this is NOT an invoicing platform" — a single
 * price line (fixed or range, QT-3's own "never silently collapsed to a single number" for a
 * range), never a line-item table (a QuotationResponse has no line items to begin with). */
export function buildQuotationPdf(quotation: QuotationResponse): jsPDF {
  const doc = new jsPDF();
  let y = 18;
  doc.setFontSize(16);
  doc.text(quotation.title, MARGIN_X, y);
  y += LINE_HEIGHT + 4;

  doc.setFontSize(11);
  const line = (label: string, value: string): void => {
    doc.text(`${label}: ${value}`, MARGIN_X, y);
    y += LINE_HEIGHT;
  };
  line('Client', quotation.party);
  if (quotation.description !== undefined) line('Description', quotation.description);
  line('Price', pricingLine(quotation));
  line('Status', quotation.status);
  if (quotation.expiryDate !== undefined)
    line('Valid until', new Date(quotation.expiryDate).toLocaleDateString());
  if (quotation.expectedStartDate !== undefined)
    line('Expected start', new Date(quotation.expectedStartDate).toLocaleDateString());
  if (quotation.paymentTerms !== undefined) line('Payment terms', quotation.paymentTerms);
  if (quotation.notes !== undefined) line('Notes', quotation.notes);

  y += 4;
  doc.setFontSize(8);
  doc.text('This is a summary only, not an invoice (QT-6).', MARGIN_X, y);

  return doc;
}

export function downloadQuotationPdf(quotation: QuotationResponse): void {
  buildQuotationPdf(quotation).save(`quotation-${quotation.id}.pdf`);
}
